"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import LocationCrosshairsIcon from "@/components/icons/LocationCrosshairsIcon";
import { Breadcrumbs, Button } from "@/components";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

function normalizeAlbertaPostal(raw: string): string {
  const compact = raw.replace(/\s+/g, "").toUpperCase();
  if (compact.length === 6) {
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
  }
  return raw.trim();
}

export default function MailOrderHeroSection() {
  const [postal, setPostal] = useState("");
  const [estimate, setEstimate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);

  const handleEstimate = async () => {
    setError(null);
    setEstimate(null);
    const normalized = normalizeAlbertaPostal(postal);
    if (!normalized || normalized.replace(/\s/g, "").length < 6) {
      setError("Enter a valid postal code.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/delivery/routing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postalCode: normalized, cartSubtotal: 50 }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setError(data?.error || "We could not check that postal code right now.");
        return;
      }
      if (data.deliveryType === "mail-order") {
        setEstimate(
          "Mail order is available for your area. Orders ship via Canada Post Xpresspost with tracking, typically 1–3 business days in Alberta."
        );
      } else if (data.deliveryType === "same-day" && data.deliveryAvailable) {
        setEstimate(
          "Your address may also qualify for same-day delivery at checkout. Mail order remains available province-wide."
        );
      } else {
        setEstimate(
          "We ship cannabis mail order across Alberta. Complete checkout with your address to see exact fulfillment options and timing."
        );
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLocateMe = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser.");
      return;
    }
    setLocLoading(true);
    setError(null);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });
      const { latitude, longitude } = position.coords;
      const geocodeRes = await fetch(
        `${API_BASE}/api/locations/reverse-geocode?lat=${latitude}&lon=${longitude}`
      );
      const geocodeData = await geocodeRes.json();
      if (!geocodeRes.ok || !geocodeData?.success || !geocodeData.postalCode) {
        setError(
          geocodeData?.error || "Could not determine postal code from your location. Enter it manually."
        );
        return;
      }
      const formatted = geocodeData.formatted || normalizeAlbertaPostal(geocodeData.postalCode);
      setPostal(formatted);
    } catch {
      setError("Unable to use your location. Please enter your postal code.");
    } finally {
      setLocLoading(false);
    }
  };

  return (
    <section className="w-full max-w-none" aria-labelledby="mail-order-hero-heading">
      <div className="w-full">
        <Breadcrumbs
          items={[
            { label: "How delivery works", href: "/how-delivery-works" },
            { label: "Mail order delivery" },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-2 lg:items-center lg:gap-12">
          <div className="min-w-0 lg:order-first">
            <h1
              id="mail-order-hero-heading"
              className="text-h2-mobile lg:text-h2-desktop font-bold leading-tight text-text-primary"
            >
              Fast &amp; Legal Alberta-Wide Mail Order Marijuana Delivery
            </h1>
            <p className="mt-4 text-body-16-desktop leading-relaxed text-text-secondary">
              We deliver premium cannabis to every postal code in Alberta via Xpresspost. No matter where you live, get
              safe, tracked shipping directly to your door. Check your estimated delivery options instantly below.
            </p>
            <div className="mt-8">
              <Button asChild variant="brand" size="lg" className="w-full sm:w-auto rounded-xl px-6 py-3">
                <Link href="/search?delivery=mail-order">
                  Shop Mail Order
                </Link>
              </Button>
            </div>
          </div>

          <div className="order-first flex min-w-0 justify-center lg:order-last lg:justify-end">
            <div className="flex w-full flex-col overflow-hidden rounded-2xl border border-blue-10 bg-constant-white shadow-[0_4px_32px_rgba(23,22,90,0.07)] relative">
              <div className="relative aspect-[4/3] max-sm:aspect-[4/4] w-full bg-blue-5">
                <Image
                  src="/media/pages/mail-order-delivery/mail-order-hero-image.webp"
                  alt="AIRDANK mail order airplane and packages"
                  fill
                  className="object-cover object-center"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="flex flex-col gap-4 p-6 max-sm:p-2 absolute bottom-4 left-4 max-sm:left-2 max-sm:bottom-2 bg-white w-[calc(100%-32px)] max-sm:w-[calc(100%-16px)] rounded-2xl">
                <h2 className="text-h4-mobile sm:text-h4-desktop font-bold text-blue-100">Check Delivery Time</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={postal}
                    onChange={(e) => setPostal(e.target.value)}
                    placeholder="Enter Postal Code"
                    className="flex-1 rounded-xl border border-neutral-10 bg-constant-white max-sm:p-2 px-4 py-3 text-body-md text-text-primary placeholder:text-text-tertiary focus:border-blue-60 focus:outline-none focus:ring-2 focus:ring-blue-15"
                    autoComplete="postal-code"
                  />
                  <Button
                    variant="icon"
                    color="white"
                    size="icon-md"
                    onClick={() => void handleLocateMe()}
                    disabled={locLoading}
                    className="shrink-0 rounded-xl !p-3 max-sm:!p-2"
                    aria-label="Use my location"
                  >
                    <LocationCrosshairsIcon size={22} color="currentColor" aria-hidden />
                  </Button>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full rounded-xl -mt-1"
                  onClick={() => void handleEstimate()}
                  disabled={loading}
                >
                  {loading ? "Checking…" : "Get Estimate"}
                </Button>
                {error ? <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
                {estimate ? <p className="mt-1 text-sm leading-relaxed text-text-secondary">{estimate}</p> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
